import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Calculator, 
  FileText, 
  Percent, 
  Check, 
  ArrowRight, 
  ShieldCheck, 
  HelpCircle, 
  Building2, 
  BadgePercent, 
  Phone, 
  Mail, 
  MapPin, 
  CheckCircle2,
  FileCheck2,
  Receipt,
  PiggyBank,
  Laptop
} from 'lucide-react'
import ContactForm from '../components/ContactForm'

import RelatedServices from '../components/RelatedServices'
import FAQSection from '../components/FAQSection'
import { updateMeta } from '../lib/utils'

export default function BookkeepingPage() {
  useEffect(() => {
    updateMeta(
      "Bogføring & Moms | Thellefsen Regnskab",
      "Professionel bogføring og moms for mindre virksomheder. Løbende afstemning af bank og bilag til en fast og gennemskuelig pris."
    )
  }, [])

  const bookkeepingServices = [
    {
      title: "Bogføring af virksomhedens indtægter og udgifter",
      desc: "Løbende og præcis registrering af alle virksomhedens transaktioner med sporbarhed til originale bilag.",
      icon: FileText
    },
    {
      title: "Kontrol af, at bilag bliver placeret på de relevante konti",
      desc: "Sikrer korrekt regnskabsmæssig kategorisering, så driftsomkostninger og aktiver bogføres rigtigt efter reglerne.",
      icon: CheckCircle2
    },
    {
      title: "Løbende afstemning af banken",
      desc: "Bankkontoen sammenholdes systematisk med bogføringen, så alle uoverensstemmelser og manglende bilag fanges i tide.",
      icon: Calculator
    },
    {
      title: "Momsregnskab og momsindberetning efter aftale",
      desc: "Korrekt beregning af købs- og salgsmoms samt rettidig indberetning til Skattestyrelsen ved hver termin.",
      icon: Percent
    }
  ]

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      
      {/* Hero Sektion */}
      <section className="bg-white border-b border-stone-200 py-16 lg:py-24 relative overflow-hidden">
        {/* Subtile dekorative baggrunds-accenter */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-orange-100/50 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 -mb-20 w-80 h-80 rounded-full bg-stone-100/60 blur-2xl pointer-events-none" />

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            {/* Top Tag */}
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-100 text-stone-700 text-xs sm:text-sm font-semibold tracking-wide uppercase mb-6 border border-stone-200">
              <span className="w-2 h-2 rounded-full bg-orange-600"></span>
              Thellefsen Regnskab &bull; Bogføring & Moms i hele Danmark
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 tracking-tight leading-tight">
              Bogføring, der giver dig mere tid til din virksomhed
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-stone-600 leading-relaxed">
              Hos{' '}
              <Link to="/" className="text-stone-900 font-semibold hover:text-orange-600 transition-colors underline decoration-stone-300 underline-offset-2">
                Thellefsen Regnskab
              </Link>{' '}
              får du hjælp til bogføring, så bilag, bank og moms bliver håndteret løbende. Vi hjælper selvstændige og mindre virksomheder med at holde styr på tallene og forstå dem. Du har direkte kontakt til Peter Thellefsen, som også står for opgaven.
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <a
                href="#kontakt-form-section"
                className="inline-flex items-center justify-center rounded-none bg-orange-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a
                href="#opgaver"
                className="inline-flex items-center justify-center rounded-none bg-stone-100 border border-stone-300 px-6 py-3.5 text-sm font-semibold text-stone-800 hover:bg-stone-200 transition-colors"
              >
                Hvad indeholder hjælpen?
              </a>
            </div>

            {/* Hurtige fordele */}
            <div className="mt-12 pt-8 border-t border-stone-200 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <Check className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Direkte kontakt</div>
                  <div className="text-xs text-stone-500">Du taler direkte med Peter</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Styr på bilag & bank</div>
                  <div className="text-xs text-stone-500">Løbende afstemning</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-orange-50 text-orange-600 border border-orange-200">
                  <BadgePercent className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-bold text-stone-900">Korrekt moms</div>
                  <div className="text-xs text-stone-500">Rettidig indberetning</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 1: Hjælp til bogføring, der passer til din hverdag */}
      <section id="opgaver" className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
              Struktur & arbejdsdeling
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight mt-2">
              Hjælp til bogføring, der passer til din hverdag
            </h2>

            <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg mt-6">
              <p>
                Bogføring er den løbende registrering af virksomhedens indtægter og udgifter. Hver post skal kunne føres tilbage til et bilag, og bevægelserne på banken skal stemme med det bogførte. Vi hjælper med at holde orden i materialet, så du har et brugbart grundlag for at følge økonomien.
              </p>
              <p>
                Du behøver ikke overlade alle økonomiopgaver til os for at få hjælp. Vi afklarer, hvilke dele du ønsker at håndtere selv, og hvad vi skal stå for. Det giver en tydelig arbejdsdeling, hvor du ved, hvad der er aftalt om bilag, afstemninger og de indberetninger, der hører til opgaven.
              </p>
            </div>
          </div>

          {/* 4 Nøgleopgaver Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {bookkeepingServices.map((item, index) => {
              const IconComp = item.icon
              return (
                <div 
                  key={index}
                  className="bg-white border border-stone-200 p-6 sm:p-8 shadow-sm hover:border-orange-500/50 hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    <div className="h-12 w-12 bg-stone-50 border border-stone-200 flex items-center justify-center text-orange-600 mb-6">
                      <IconComp className="h-6 w-6" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-stone-900 leading-snug mb-3">
                      {item.title}
                    </h3>
                    <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-stone-100 flex items-center gap-2 text-xs font-semibold text-stone-500">
                    <Check className="h-4 w-4 text-orange-600" />
                    <span>Aftales efter dit konkrete behov</span>
                  </div>
                </div>
              )
            })}
          </div>

        </div>
      </section>

      {/* Sektion 2 & 3: Momsindberetning & Enkeltmandsvirksomhed */}
      <section className="py-16 lg:py-24 bg-white border-y border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            
            {/* Kort 1: Momsindberetning med styr på grundlaget */}
            <div className="bg-stone-50 border border-stone-200 p-8 sm:p-10 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-stone-200 text-xs font-semibold text-stone-600 uppercase tracking-wide mb-4">
                  <Percent className="h-3.5 w-3.5 text-orange-600" />
                  Moms & bilagsdokumentation
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mb-6">
                  Momsindberetning med styr på grundlaget
                </h3>
                <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg">
                  <p>
                    Når din virksomhed er momsregistreret, er den løbende bogføring grundlaget for momsregnskabet. Vi gennemgår købsmoms og salgsmoms og hjælper med momsindberetning ved de relevante terminer. Det er vigtigt at skelne mellem udgifter med fuldt momsfradrag, delvist fradrag og intet fradrag.
                  </p>
                  <p>
                    Et bilag giver ikke automatisk ret til at trække momsen fra. Både udgiftens formål og dokumentationen har betydning. Hvis der mangler oplysninger, skal de afklares, før momsen kan behandles korrekt. Derfor er det en fordel at få spørgsmål til bilag afklaret løbende frem for lige før momsfristen.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200/80">
                <div className="text-sm font-semibold text-stone-900 mb-2">
                  Husk om momsfradrag:
                </div>
                <ul className="space-y-2 text-sm text-stone-600">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-orange-600 shrink-0" />
                    <span>Fuldt momsfradrag på rene driftsudgifter og varekøb</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-orange-600 shrink-0" />
                    <span>Delvist momsfradrag (f.eks. ved blandet erhverv/privat brug eller hotel)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-orange-600 shrink-0" />
                    <span>Intet fradrag (f.eks. repræsentation som restaurationsbesøg uden forretningsmøde)</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Kort 2: Bogføring i din enkeltmandsvirksomhed */}
            <div className="bg-stone-50 border border-stone-200 p-8 sm:p-10 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-stone-200 text-xs font-semibold text-stone-600 uppercase tracking-wide mb-4">
                  <Building2 className="h-3.5 w-3.5 text-orange-600" />
                  Enkeltmandsvirksomheder
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mb-6">
                  Bogføring i din enkeltmandsvirksomhed
                </h3>
                <div className="space-y-4 text-stone-600 leading-relaxed text-base sm:text-lg">
                  <p>
                    I en enkeltmandsvirksomhed kan private og erhvervsmæssige betalinger let komme til at stå mellem hinanden. Vi hjælper med at skelne mellem virksomhedens udgifter og dine private poster. Private hævninger er eksempelvis ikke driftsudgifter og skal derfor behandles anderledes i bogføringen.
                  </p>
                  <p>
                    Du kan få hjælp til den daglige registrering eller samle flere opgaver hos os. Når bilag og bank er afstemt undervejs, bliver grundlaget for årets afslutning mere overskueligt. Har du også brug for hjælp med årets tal, kan du{' '}
                    <Link 
                      to="/regnskabsservice" 
                      className="font-semibold text-orange-600 hover:text-orange-700 underline decoration-orange-300 underline-offset-4 transition-colors"
                    >
                      læse mere om vores regnskabsservice og mulighederne for en samlet løsning
                    </Link>.
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-stone-200/80 bg-white p-5 border border-stone-200">
                <div className="flex items-start gap-3">
                  <PiggyBank className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-bold text-stone-900">Adskil privat og erhverv</div>
                    <p className="text-xs text-stone-600 mt-1 leading-relaxed">
                      Ved at have en fast erhvervskonto og lade Peter afstemme dine bilag løbende, undgår du skattemæssige problemer ved årsafslutningen.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Sektion 4: Fast årlig pris på dit samlede regnskab */}
      <section className="py-16 lg:py-24 bg-stone-100/70 border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-4xl mx-auto bg-white border border-stone-200 p-8 sm:p-12 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl pointer-events-none" />

            <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 text-orange-700 text-xs font-semibold tracking-wide uppercase mb-4 border border-orange-200">
              <Laptop className="h-3.5 w-3.5" />
              Samlet regnskabspakke
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mb-4">
              En fast årlig pris på dit samlede regnskab
            </h2>

            <p className="text-stone-600 text-base sm:text-lg leading-relaxed mb-6">
              Ønsker du at samle opgaverne, tilbyder vi en regnskabspakke til en fast årlig pris. Den omfatter bogføring, bankafstemning, momsindberetning, årsregnskab og relevante skatteoplysninger. Adgang til regnskabsprogram indgår uden ekstra omkostninger som en del af den samlede pakke.
            </p>

            <p className="text-stone-600 text-base sm:text-lg leading-relaxed mb-8">
              Vi afklarer indholdet ud fra din virksomhedsform, så de aftalte leverancer passer til din virksomhed. Der er forskel på årets afslutning for en enkeltmandsvirksomhed og et selskab. Du kan{' '}
              <Link 
                to="/aarsrapport-og-aarsregnskab" 
                className="font-semibold text-orange-600 hover:text-orange-700 underline decoration-orange-300 underline-offset-4 transition-colors"
              >
                læse mere om arbejdet med årsrapport og årsafslutning
              </Link>
              , hvis du også ønsker hjælp til den del.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6 border-t border-stone-200">
              <Link
                to="/regnskabsservice"
                className="inline-flex items-center justify-between p-4 bg-stone-50 border border-stone-200 hover:border-orange-500/50 hover:bg-stone-100 transition-all text-sm font-semibold text-stone-900"
              >
                <span>Se den samlede regnskabspakke</span>
                <ArrowRight className="h-4 w-4 text-orange-600" />
              </Link>
              <Link
                to="/aarsrapport-og-aarsregnskab"
                className="inline-flex items-center justify-between p-4 bg-stone-50 border border-stone-200 hover:border-orange-500/50 hover:bg-stone-100 transition-all text-sm font-semibold text-stone-900"
              >
                <span>Læs om årsrapport & årsafslutning</span>
                <ArrowRight className="h-4 w-4 text-orange-600" />
              </Link>
            </div>

          </div>



        </div>
      </section>

      {/* Sektion 5: Hvad koster bogføring hos Thellefsen Regnskab? */}
      <section className="py-16 lg:py-20 bg-white border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center p-3 bg-stone-100 border border-stone-200 mb-6 text-orange-600">
              <BadgePercent className="h-8 w-8" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-stone-900 tracking-tight">
              Hvad koster bogføring hos Thellefsen Regnskab?
            </h2>
            <p className="mt-6 text-stone-600 text-base sm:text-lg leading-relaxed">
              Prisen afhænger af opgavens omfang og den hjælp, du har brug for. Antallet af bilag, behovet for afstemninger og fordelingen af arbejdet har betydning for tilbuddet. Det samme gælder, hvis der først skal ryddes op i tidligere bogføring, før de løbende opgaver kan begynde.
            </p>
            <p className="mt-4 text-stone-600 text-base sm:text-lg leading-relaxed">
              Kontakt os og fortæl om din virksomhed, dit bilagsomfang og de opgaver, du gerne vil have hjælp til. Så kan vi afklare behovet og give et uforpligtende tilbud på et tydeligt grundlag. Du får dermed mulighed for at tage stilling til både pris og indhold, inden du beslutter dig.
            </p>
            <div className="mt-8">
              <a
                href="#kontakt-form-section"
                className="inline-flex items-center justify-center rounded-none bg-orange-600 px-6 py-3.5 text-sm font-semibold text-white hover:bg-orange-700 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                Indhent et uforpligtende tilbud her
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 6: FAQ */}
      <FAQSection
        title="Ofte stillede spørgsmål om bogføring og moms"
        subtitle="Få svar på de mest almindelige spørgsmål om bilagshåndtering, momsindberetning og digital bogføring."
        items={[
          {
            question: "Hvor ofte skal jeg indsende mine bilag og kvitteringer til jer?",
            answer: "Vi anbefaler, at du uploader eller videresender dine bilag løbende eller én gang om måneden, så regnskabet forbliver frisk og overskueligt. Med et moderne regnskabsprogram tager du blot et billede af kvitteringen med din mobiltelefon eller videresender PDF-fakturaer direkte via e-mail, hvorefter vi sørger for den korrekte kontering."
          },
          {
            question: "Hvordan håndterer I momsindberetning for min virksomhed?",
            answer: "Når bogføringen for momsperioden er afsluttet, afstemmer vi alle momskonti mod banken og udarbejder en klar momsopgørelse. Vi gennemgår tallene med dig og sikrer, at indberetningen til Skattestyrelsen sker præcist og inden fristen, så du altid ved nøjagtigt, hvad du skal betale eller have tilbage i moms."
          },
          {
            question: "Kan I hjælpe, hvis min bogføring er kommet bagud?",
            answer: "Ja, det oplever vi ofte, og vi hjælper dig hurtigt tilbage på sporet. Uanset om du mangler bogføring for enkelte måneder eller et helt år, gennemgår vi dine bankkonti og bilagsbunker systematisk, retter eventuelle fejl og sikrer, at du overholder gældende regler og undgår unødige gebyrer fra myndighederne."
          },
          {
            question: "Hvilke regnskabsprogrammer arbejder I med til daglig bogføring?",
            answer: "Vi arbejder primært med de mest udbredte og brugervenlige systemer som Dinero og Billy, men vi servicerer også kunder på e-conomic og andre digitale platforme. Hvis du ikke allerede har et program, hjælper vi med opsætningen og sikrer den mest effektive integration til din netbank og kassesystem."
          },
          {
            question: "Overholder jeres bogføring kravene i den nye bogføringslov?",
            answer: "Ja, vi sikrer 100 % overholdelse af den nye bogføringslovs skærpede krav til digital opbevaring, bilagskobling og IT-sikkerhedskopiering. Alle dine bilag bliver digitalt arkiveret og koblet direkte til de enkelte posteringer i systemet, så din virksomhed er fuldt lovlig og altid klar til eventuel kontrol."
          }
        ]}
      />

      {/* Sektion 7: Relaterede ydelser */}
      <RelatedServices currentPath="/bogfoering" title="Se også vores andre ydelser" />

      {/* Sektion 8: Din bogholder på Sjælland og i resten af Danmark & Kontakt */}
      <section id="kontakt-form-section" className="py-16 lg:py-24 bg-stone-50 border-t border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Tekst og info */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-orange-600">
                  Lokal og landsdækkende bogholder
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-2">
                  Din bogholder på Sjælland og i resten af Danmark
                </h2>

                <div className="space-y-4 text-stone-600 text-base sm:text-lg leading-relaxed mt-4">
                  <p>
                    Vi holder til i Espergærde og hjælper virksomheder i Nordsjælland, på resten af Sjælland og rundt om i Danmark. Din virksomhed behøver derfor ikke ligge i nærheden. Det afgørende er, at vi får aftalt en arbejdsdeling og en kontaktform, der passer til dig og dine regnskabsopgaver.
                  </p>
                  <p>
                    Hos os taler du direkte med Peter, som kender opgaven og kan forklare tallene i et sprog, du forstår. Har du brug for løbende bogføring eller hjælp til at komme ajour, er du velkommen til at kontakte os om din bogføring. Vi tager en uforpligtende snak om, hvad du har brug for.
                  </p>
                </div>

                {/* Profilkort Peter */}
                <div className="mt-8 bg-white border border-stone-200 p-6 shadow-sm">
                  <div className="flex items-center gap-4 mb-4">
                    <img 
                      src="https://assets.auxodev.dk/assets/57eb0739-2c0e-4813-8144-be81ba542e2d/img-4119.jpg" 
                      alt="Peter Thellefsen - Revisor og indehaver af Thellefsen Regnskab ApS" 
                      className="w-16 h-16 object-cover border border-stone-200 shrink-0"
                    />
                    <div>
                      <div className="font-bold text-stone-900 text-base">Peter Thellefsen</div>
                      <div className="text-xs text-stone-500">Revisor & Indehaver</div>
                      <div className="text-xs text-orange-600 font-medium mt-0.5">Thellefsen Regnskab ApS</div>
                    </div>
                  </div>

                  <div className="space-y-2.5 pt-3 border-t border-stone-100 text-xs sm:text-sm text-stone-600">
                    <div className="flex items-center gap-2.5">
                      <Phone className="h-4 w-4 text-orange-600 shrink-0" />
                      <a href="tel:+4531335470" className="hover:text-orange-600 transition-colors font-medium">
                        31 33 54 70
                      </a>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Mail className="h-4 w-4 text-orange-600 shrink-0" />
                      <a href="mailto:info@tregnskab.dk" className="hover:text-orange-600 transition-colors">
                        info@tregnskab.dk
                      </a>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <MapPin className="h-4 w-4 text-orange-600 shrink-0" />
                      <span>Strandvangsvej 9, 3060 Espergærde</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* Kontaktformular */}
            <div className="lg:col-span-7">
              <ContactForm 
                defaultService="bogfoering" 
                title="Få et uforpligtende tilbud på bogføring" 
                subtitle="Fortæl kort om din virksomhed og dit bilagsomfang, så vender Peter tilbage med et skræddersyet tilbud." 
              />
            </div>

          </div>

        </div>
      </section>

    </div>
  )
}
